import { SDK_ERRORS } from '@riz/contracts/sdk';
import { rejectWith } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';

export interface GameModule {
  start(details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  pause(details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  resume(details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  end(score?: number, details?: Record<string, unknown>): Promise<Record<string, unknown>>;
  completedPercentage(percentage: number): Promise<Record<string, unknown>>;
  setContext(context: Record<string, unknown>): void;
  clearContext(): void;
  celebrate(reason?: string): void;
  requestFullscreen(): void;
  exitFullscreen(): void;
  setMuted(muted: boolean): void;
  isMuted(): boolean;
  firstFrame(): Promise<Record<string, unknown>>;
}

export function createGameModule(client: SdkClient): GameModule {
  let context: Record<string, unknown> = {};
  let firstFrameReported = false;
  const startedAt = now();

  return {
    start(details) {
      client.markRunStarted();
      return client.track('game_started', { ...details, context });
    },

    pause(details) {
      return client.track('game_paused', { ...details, context });
    },

    resume(details) {
      return client.track('game_resumed', { ...details, context });
    },

    end(score, details) {
      const resolved = score === undefined ? client.bestScoreSoFar : clampScore(score);
      if (resolved === null) {
        return rejectWith(SDK_ERRORS.invalidPayload, 'game.end(score) needs a finite number.');
      }
      const best = client.raiseBestScore(resolved);
      client.markRunEnded();
      return client.track('game_ended', { ...details, score: best, context });
    },

    completedPercentage(percentage) {
      const value = Number(percentage);
      if (!Number.isFinite(value)) {
        return rejectWith(SDK_ERRORS.invalidPayload, 'completedPercentage needs a number between 0 and 100.');
      }
      return client.track('game_completed_percentage', {
        percentage: Math.max(0, Math.min(100, Math.round(value))),
      });
    },

    /**
     * Attached to every subsequent lifecycle event and to any report the player
     * files, which is the difference between a reproducible bug and "it broke".
     */
    setContext(next) {
      context = next && typeof next === 'object' ? { ...next } : {};
    },

    clearContext() {
      context = {};
    },

    celebrate(reason) {
      client.notifyHost('riz:celebrate', { reason: reason ?? 'achievement' });
    },

    requestFullscreen() {
      client.notifyHost('riz:fullscreen', { action: 'enter' });
    },

    exitFullscreen() {
      client.notifyHost('riz:fullscreen', { action: 'exit' });
    },

    setMuted(muted) {
      client.setMuted(Boolean(muted));
      client.notifyHost('riz:mute', { muted: Boolean(muted) });
    },

    isMuted() {
      return client.isMuted;
    },

    /**
     * Reported once. The gap between init and the first rendered frame is the
     * number the technical requirements hold a game to, so a game that never
     * calls this cannot be measured and is treated as failing the budget.
     */
    firstFrame() {
      if (firstFrameReported) return Promise.resolve({ accepted: true, duplicate: true });
      firstFrameReported = true;
      return client.track('first_frame_rendered', { elapsedMs: Math.round(now() - startedAt) });
    },
  };
}

export function clampScore(value: unknown): number | null {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.floor(numeric));
}

function now(): number {
  try {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  } catch {
    return Date.now();
  }
}
